import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Tag, Palette, AlertCircle, Loader2, CheckCircle2, LayoutGrid } from 'lucide-react';
import { categoriesApi } from '@/api/categoriesApi';
import { handleApiError, getApiErrorDetails } from '@/api/client';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import type { Category } from '@/types/api';

interface CategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  category?: Category | null;
}

interface FormData {
  name: string;
  description: string;
  color: string;
  sortOrder: string;
}

interface FormErrors {
  name?: string;
  sortOrder?: string;
  submit?: string;
}

const PRESET_COLORS = [
  '#3B82F6', // Blue
  '#EF4444', // Red
  '#10B981', // Green
  '#F59E0B', // Amber
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#06B6D4', // Cyan
  '#F97316', // Orange
  '#6366F1', // Indigo
  '#84CC16', // Lime
  '#14B8A6', // Teal
  '#DC2626', // Red Dark
];

export function CategoryModal({ isOpen, onClose, onSuccess, category }: CategoryModalProps) {
  const queryClient = useQueryClient();
  const isEditMode = !!category;
  
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    color: '#3B82F6',
    sortOrder: '0',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (category) {
      setFormData({
        name: category.name || '',
        description: category.description || '',
        color: category.color || '#3B82F6',
        sortOrder: category.sortOrder?.toString() || '0',
      });
    } else {
      setFormData({
        name: '',
        description: '',
        color: '#3B82F6',
        sortOrder: '0',
      });
    }
    setErrors({});
    setTouched({});
  }, [category, isOpen]);

  const createMutation = useMutation({
    mutationFn: categoriesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      onSuccess?.();
      handleClose();
    },
    onError: (err: any) => {
      const errorDetails = getApiErrorDetails(err);
      
      // Handle field-specific validation errors
      if (errorDetails.isValidationError && Object.keys(errorDetails.fieldErrors).length > 0) {
        const fieldErrors: Partial<FormErrors> = {};
        Object.entries(errorDetails.fieldErrors).forEach(([field, message]) => {
          if (field === 'name') fieldErrors.name = message;
          else if (field === 'sortOrder') fieldErrors.sortOrder = message;
        });
        setErrors(prev => ({ ...prev, ...fieldErrors, submit: 'Please fix the validation errors below.' }));
      } else {
        const message = handleApiError(err);
        setErrors(prev => ({ ...prev, submit: message }));
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<Category>) => categoriesApi.update(category!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      onSuccess?.();
      handleClose();
    },
    onError: (err: any) => {
      const errorDetails = getApiErrorDetails(err);
      
      // Handle field-specific validation errors
      if (errorDetails.isValidationError && Object.keys(errorDetails.fieldErrors).length > 0) {
        const fieldErrors: Partial<FormErrors> = {};
        Object.entries(errorDetails.fieldErrors).forEach(([field, message]) => {
          if (field === 'name') fieldErrors.name = message;
          else if (field === 'sortOrder') fieldErrors.sortOrder = message;
        });
        setErrors(prev => ({ ...prev, ...fieldErrors, submit: 'Please fix the validation errors below.' }));
      } else {
        const message = handleApiError(err);
        setErrors(prev => ({ ...prev, submit: message }));
      }
    },
  });

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Category name is required';
    }

    const sortOrder = parseInt(formData.sortOrder);
    if (isNaN(sortOrder) || sortOrder < 0) {
      newErrors.sortOrder = 'Sort order must be a positive number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      const data = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        color: formData.color,
        sortOrder: parseInt(formData.sortOrder),
      };
      
      if (isEditMode) {
        updateMutation.mutate(data);
      } else {
        createMutation.mutate(data);
      }
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      color: '#3B82F6',
      sortOrder: '0',
    });
    setErrors({});
    setTouched({});
    createMutation.reset();
    updateMutation.reset();
    onClose();
  };

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setTouched((prev) => ({ ...prev, [field]: true }));
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  if (!isOpen) return null;

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-gray-900/60 dark:bg-charcoal-950/80 backdrop-blur-sm animate-fade-in"
        onClick={!isPending ? handleClose : undefined}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-lg max-h-[90vh] overflow-hidden rounded-2xl bg-white dark:bg-charcoal-900 border border-gray-200 dark:border-charcoal-700 shadow-2xl animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200 dark:border-charcoal-700 bg-gray-50 dark:bg-charcoal-850">
          <div className="flex items-center gap-4">
            <div 
              className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg"
              style={{ backgroundColor: `${formData.color}30` }}
            >
              <LayoutGrid className="w-6 h-6" style={{ color: formData.color }} />
            </div>
            <div>
              <h2 className="font-display text-xl font-bold text-gray-900 dark:text-warm-cream">
                {isEditMode ? 'Edit Category' : 'Add New Category'}
              </h2>
              <p className="text-sm text-gray-600 dark:text-warm-sand">
                {isEditMode ? 'Update category details' : 'Create a product category'}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={isPending}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-100 dark:bg-charcoal-700 rounded-xl transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5 text-gray-600 dark:text-warm-sand" />
          </button>
        </div>

        {/* Error Alert */}
        {(errors.submit || errors.name || errors.sortOrder || createMutation.isError || updateMutation.isError) && (
          <div className="mx-6 mt-4">
            <ErrorAlert
              title={errors.name || errors.sortOrder ? 'Please fix the errors below' : `Failed to ${isEditMode ? 'update' : 'create'} category`}
              message={errors.submit}
              fieldErrors={{
                ...(errors.name && { name: errors.name }),
                ...(errors.sortOrder && { sortOrder: errors.sortOrder }),
              }}
              severity={errors.name || errors.sortOrder ? 'warning' : 'error'}
            />
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="space-y-5">
            {/* Category Name */}
            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-warm-cream mb-2">
                Category Name <span className="text-danger-400">*</span>
              </label>
              <div className="relative">
                <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-taupe" />
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="e.g., Home Decor"
                  className={`
                    w-full pl-10 pr-4 py-3 bg-white dark:bg-charcoal-800 border rounded-xl text-gray-900 dark:text-warm-cream 
                    placeholder-gray-400 dark:placeholder-gray-400 dark:placeholder-charcoal-500 focus:outline-none focus:ring-2 focus:ring-amber-450/30 
                    transition-all
                    ${errors.name && touched.name ? 'border-danger-500' : 'border-gray-300 dark:border-charcoal-600 focus:border-amber-450'}
                  `}
                  autoFocus
                />
              </div>
              {errors.name && touched.name && (
                <p className="mt-1.5 text-sm text-danger-400 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.name}
                </p>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-warm-cream mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Brief description of this category..."
                rows={2}
                className="w-full px-4 py-3 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-xl text-gray-900 dark:text-warm-cream placeholder-gray-400 dark:placeholder-gray-400 dark:placeholder-charcoal-500 focus:outline-none focus:ring-2 focus:ring-amber-450/30 focus:border-amber-450 transition-all resize-none"
              />
            </div>

            {/* Color Picker */}
            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-warm-cream mb-2">
                Color
              </label>
              <div className="flex flex-wrap gap-2">
                {PRESET_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => handleChange('color', color)}
                    className={`
                      w-8 h-8 rounded-lg border-2 transition-all
                      ${formData.color === color 
                        ? 'border-white scale-110' 
                        : 'border-transparent hover:scale-105'
                      }
                    `}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
              <div className="mt-3 flex items-center gap-3">
                <Palette className="w-4 h-4 text-warm-taupe" />
                <input
                  type="text"
                  value={formData.color}
                  onChange={(e) => handleChange('color', e.target.value)}
                  placeholder="#3B82F6"
                  className="flex-1 px-3 py-2 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-lg text-gray-900 dark:text-warm-cream text-sm font-mono uppercase"
                />
                <div 
                  className="w-8 h-8 rounded-lg border border-gray-300 dark:border-charcoal-600"
                  style={{ backgroundColor: formData.color }}
                />
              </div>
            </div>

            {/* Sort Order */}
            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-warm-cream mb-2">
                Sort Order
              </label>
              <input
                type="number"
                min="0"
                value={formData.sortOrder}
                onChange={(e) => handleChange('sortOrder', e.target.value)}
                className={`
                  w-full px-4 py-3 bg-white dark:bg-charcoal-800 border rounded-xl text-gray-900 dark:text-warm-cream 
                  placeholder-gray-400 dark:placeholder-gray-400 dark:placeholder-charcoal-500 focus:outline-none focus:ring-2 focus:ring-amber-450/30 
                  transition-all
                  ${errors.sortOrder ? 'border-danger-500' : 'border-gray-300 dark:border-charcoal-600 focus:border-amber-450'}
                `}
              />
              {errors.sortOrder && (
                <p className="mt-1.5 text-sm text-danger-400">{errors.sortOrder}</p>
              )}
              <p className="mt-1.5 text-xs text-gray-600 dark:text-warm-sand">
                Lower numbers appear first in the list.
              </p>
            </div>

            {/* Preview */}
            <div className="p-4 bg-white dark:bg-charcoal-800/50 border border-gray-200 dark:border-charcoal-700 rounded-xl">
              <p className="text-xs text-gray-600 dark:text-warm-sand mb-3">Preview</p>
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${formData.color}20`, border: `1px solid ${formData.color}40` }}
                >
                  <Tag className="w-5 h-5" style={{ color: formData.color }} />
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-warm-cream">
                    {formData.name || 'Category Name'}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-warm-sand">
                    {formData.description || 'No description'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-6 mt-6 border-t border-gray-200 dark:border-charcoal-700">
            <button
              type="button"
              onClick={handleClose}
              className="btn-secondary"
              disabled={isPending}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary min-w-[140px]"
              disabled={isPending}
            >
              {isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {isEditMode ? 'Saving...' : 'Creating...'}
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  {isEditMode ? 'Save Changes' : 'Create Category'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
